/** SUNTRONIX ONE — Data Trust tab for Master Customer 360.
 *  Configures Retention · Privacy · Source-of-Truth · Logistics lanes
 *  so the runtime gates have a fast configuration surface.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Save, Loader2, Plus, ShieldCheck, Globe2, Trash2 } from "lucide-react";
import Section from "@/components/app/Section";

export default function DataTrustTab({ masterClientId }) {
  const [sub, setSub] = useState("retention");
  const TABS = [
    { key: "retention", label: "Retention" },
    { key: "privacy",   label: "Privacy" },
    { key: "sot",       label: "Source of Truth" },
    { key: "logistics", label: "Logistics Lanes" },
  ];
  return (
    <div className="space-y-4" data-testid="data-trust-tab">
      <div className="border-b border-slate-200 flex gap-1">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setSub(t.key)}
            data-testid={`data-trust-sub-${t.key}`}
            className={"px-3 py-2 text-[12px] font-medium border-b-2 -mb-px " +
              (sub === t.key
                ? "border-slate-900 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-700")}>
            {t.label}
          </button>
        ))}
      </div>
      {sub === "retention" && <RetentionPanel mcId={masterClientId} />}
      {sub === "privacy"   && <PrivacyPanel   mcId={masterClientId} />}
      {sub === "sot"       && <SoTPanel       mcId={masterClientId} />}
      {sub === "logistics" && <LogisticsPanel mcId={masterClientId} />}
    </div>
  );
}

function RetentionPanel({ mcId }) {
  const [items, setItems] = useState([]);
  const [domains, setDomains] = useState([]);
  const [draft, setDraft] = useState({ domain: "", retain_days: 365, legal_basis: "" });
  const load = useCallback(() => {
    api.get(`/data-trust/retention/${mcId}`).then(({ data }) => {
      setItems(data.items); setDomains(data.supported_domains);
    }).catch(() => {});
  }, [mcId]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    if (!draft.domain) return toast.error("Domain required");
    try {
      await api.post("/data-trust/retention", { master_company_id: mcId, ...draft });
      toast.success("Saved"); setDraft({ domain: "", retain_days: 365, legal_basis: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Section title="Retention rules" hint="Days each data domain is retained before purge candidates are surfaced." testId="retention-section">
      <div className="st-panel p-3 grid grid-cols-4 gap-2 items-end mb-3">
        <div>
          <Label className="text-[10px] uppercase text-slate-500">Domain</Label>
          <Select value={draft.domain} onValueChange={(v) => setDraft({...draft, domain: v})}>
            <SelectTrigger data-testid="ret-domain"><SelectValue placeholder="Choose…" /></SelectTrigger>
            <SelectContent>{domains.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-[10px] uppercase text-slate-500">Retain days</Label>
          <Input type="number" value={draft.retain_days} onChange={(e) => setDraft({...draft, retain_days: Number(e.target.value)})} data-testid="ret-days" /></div>
        <div><Label className="text-[10px] uppercase text-slate-500">Legal basis</Label>
          <Input value={draft.legal_basis} onChange={(e) => setDraft({...draft, legal_basis: e.target.value})} placeholder="e.g. GDPR Art 5(1)(e)" data-testid="ret-basis" /></div>
        <Button onClick={save} data-testid="ret-save"><Plus className="h-3.5 w-3.5 mr-1" /> Save</Button>
      </div>
      <table className="w-full text-[12px] st-panel">
        <thead className="bg-slate-50"><tr><th className="text-left px-2 py-1">Domain</th><th className="text-right px-2 py-1">Days</th><th className="text-left px-2 py-1">Legal basis</th><th className="text-left px-2 py-1">Updated</th></tr></thead>
        <tbody>{items.map(i => (
          <tr key={i.id} className="border-t"><td className="px-2 py-1">{i.domain}</td><td className="px-2 py-1 text-right font-mono">{i.retain_days}</td><td className="px-2 py-1">{i.legal_basis || "—"}</td><td className="px-2 py-1 text-[10px] text-slate-500">{i.updated_by}</td></tr>
        ))}</tbody>
      </table>
    </Section>
  );
}

function PrivacyField({ k, label, ph, form, setForm }) {
  return (
    <div>
      <Label className="text-[10px] uppercase text-slate-500">{label}</Label>
      <Input value={form[k] || ""} onChange={(e) => setForm({...form, [k]: e.target.value})} placeholder={ph} data-testid={`priv-${k}`} />
    </div>
  );
}

function PrivacyPanel({ mcId }) {
  const [form, setForm] = useState({});
  useEffect(() => {
    api.get(`/data-trust/privacy/${mcId}`).then(({ data }) => setForm(data)).catch(() => {});
  }, [mcId]);
  const save = async () => {
    try {
      await api.post("/data-trust/privacy", { ...form, master_company_id: mcId,
        data_residency: (form.data_residency_str || form.data_residency || "").toString().split(",").map(s => s.trim()).filter(Boolean),
        processing_countries: (form.processing_countries_str || form.processing_countries || "").toString().split(",").map(s => s.trim()).filter(Boolean),
      });
      toast.success("Privacy profile saved");
    } catch { toast.error("Save failed"); }
  };
  return (
    <Section title="Privacy profile" testId="privacy-section">
      <div className="st-panel p-4 grid grid-cols-2 gap-3">
        <PrivacyField k="pii_access_class" label="PII access class" ph="public / restricted / confidential" form={form} setForm={setForm} />
        <PrivacyField k="data_classification" label="Data classification" ph="public / internal / restricted / secret" form={form} setForm={setForm} />
        <PrivacyField k="data_residency_str" label="Data residency (ISO2, comma)" ph="IN, EU, US" form={form} setForm={setForm} />
        <PrivacyField k="processing_countries_str" label="Processing countries" ph="IN, US" form={form} setForm={setForm} />
        <PrivacyField k="privacy_contact_email" label="Privacy contact email" ph="privacy@example.com" form={form} setForm={setForm} />
        <PrivacyField k="privacy_contact_name" label="Privacy contact name" form={form} setForm={setForm} />
        <PrivacyField k="dpa_signed_at" label="DPA signed at (YYYY-MM-DD)" form={form} setForm={setForm} />
        <div className="flex items-end"><Button onClick={save} data-testid="priv-save"><Save className="h-3.5 w-3.5 mr-1" /> Save</Button></div>
      </div>
    </Section>
  );
}

function SoTPanel({ mcId }) {
  const [items, setItems] = useState([]); const [domains, setDomains] = useState([]); const [sources, setSources] = useState([]);
  const [draft, setDraft] = useState({ domain: "", source: "" });
  const load = () => api.get(`/data-trust/sot-matrix/${mcId}`).then(({data}) => {
    setItems(data.items); setDomains(data.domains); setSources(data.sources);
  });
  useEffect(load, [mcId]);
  const save = async () => {
    if (!draft.domain || !draft.source) return toast.error("Domain + source required");
    try { await api.post("/data-trust/sot-matrix", { master_company_id: mcId, ...draft }); toast.success("Saved"); setDraft({domain:"",source:""}); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Section title="Source of Truth Matrix" hint="Authoritative source system per data domain." testId="sot-section">
      <div className="st-panel p-3 grid grid-cols-3 gap-2 items-end mb-3">
        <div><Label className="text-[10px] uppercase text-slate-500">Domain</Label>
          <Select value={draft.domain} onValueChange={(v) => setDraft({...draft, domain: v})}>
            <SelectTrigger data-testid="sot-domain"><SelectValue /></SelectTrigger>
            <SelectContent>{domains.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-[10px] uppercase text-slate-500">Source</Label>
          <Select value={draft.source} onValueChange={(v) => setDraft({...draft, source: v})}>
            <SelectTrigger data-testid="sot-source"><SelectValue /></SelectTrigger>
            <SelectContent>{sources.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select></div>
        <Button onClick={save} data-testid="sot-save"><Plus className="h-3.5 w-3.5 mr-1" /> Save</Button>
      </div>
      <table className="w-full text-[12px] st-panel">
        <thead className="bg-slate-50"><tr><th className="text-left px-2 py-1">Domain</th><th className="text-left px-2 py-1">Source</th><th className="text-left px-2 py-1">Updated by</th></tr></thead>
        <tbody>{items.map(i => (
          <tr key={i.domain} className="border-t"><td className="px-2 py-1">{i.domain}</td><td className="px-2 py-1">{i.source}</td><td className="px-2 py-1 text-[10px] text-slate-500">{i.updated_by}</td></tr>
        ))}</tbody>
      </table>
    </Section>
  );
}

function LogisticsPanel({ mcId }) {
  const [items, setItems] = useState([]); const [incoterms, setIncoterms] = useState([]);
  const [draft, setDraft] = useState({ origin_country: "", destination_country: "", incoterm: "", ior_party: "", eor_party: "", duties_paid_by: "", restricted: false });
  const load = () => api.get(`/data-trust/logistics/${mcId}`).then(({data}) => { setItems(data.items); setIncoterms(data.incoterms); });
  useEffect(load, [mcId]);
  const save = async () => {
    if (!draft.origin_country || !draft.destination_country) return toast.error("Origin + destination required");
    try {
      await api.post("/data-trust/logistics", { master_company_id: mcId, ...draft });
      toast.success("Lane saved");
      setDraft({ origin_country: "", destination_country: "", incoterm: "", ior_party: "", eor_party: "", duties_paid_by: "", restricted: false });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Section title="Logistics lanes"
      hint="Per-country-pair IOR / EOR / Incoterm.  Cross-border delivery updates are refused without a lane."
      testId="logistics-section">
      <div className="st-panel p-3 grid grid-cols-6 gap-2 items-end mb-3">
        <div><Label className="text-[10px] uppercase text-slate-500">Origin</Label>
          <Input value={draft.origin_country} onChange={(e) => setDraft({...draft, origin_country: e.target.value.toUpperCase()})} placeholder="IN" maxLength={2} data-testid="lane-origin" /></div>
        <div><Label className="text-[10px] uppercase text-slate-500">Destination</Label>
          <Input value={draft.destination_country} onChange={(e) => setDraft({...draft, destination_country: e.target.value.toUpperCase()})} placeholder="US" maxLength={2} data-testid="lane-dest" /></div>
        <div><Label className="text-[10px] uppercase text-slate-500">Incoterm</Label>
          <Select value={draft.incoterm} onValueChange={(v) => setDraft({...draft, incoterm: v})}>
            <SelectTrigger data-testid="lane-incoterm"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>{incoterms.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-[10px] uppercase text-slate-500">IOR</Label>
          <Input value={draft.ior_party} onChange={(e) => setDraft({...draft, ior_party: e.target.value})} placeholder="Suntronix / Customer" data-testid="lane-ior" /></div>
        <div><Label className="text-[10px] uppercase text-slate-500">EOR</Label>
          <Input value={draft.eor_party} onChange={(e) => setDraft({...draft, eor_party: e.target.value})} data-testid="lane-eor" /></div>
        <Button onClick={save} data-testid="lane-save"><Plus className="h-3.5 w-3.5 mr-1" /> Save lane</Button>
      </div>
      <table className="w-full text-[12px] st-panel">
        <thead className="bg-slate-50"><tr><th className="text-left px-2 py-1">Route</th><th className="text-left px-2 py-1">Incoterm</th><th className="text-left px-2 py-1">IOR</th><th className="text-left px-2 py-1">EOR</th><th className="text-left px-2 py-1">Duties</th><th className="text-left px-2 py-1">Flags</th></tr></thead>
        <tbody>{items.map(i => (
          <tr key={i.id} className="border-t">
            <td className="px-2 py-1 font-mono">{i.origin_country} → {i.destination_country}</td>
            <td className="px-2 py-1">{i.incoterm || "—"}</td>
            <td className="px-2 py-1">{i.ior_party || "—"}</td>
            <td className="px-2 py-1">{i.eor_party || "—"}</td>
            <td className="px-2 py-1">{i.duties_paid_by || "—"}</td>
            <td className="px-2 py-1">
              {i.restricted && <span className="text-red-700 text-[10px] font-semibold">RESTRICTED</span>}
              {i.insurance_required && <span className="ml-1 text-amber-700 text-[10px]">INSURED</span>}
            </td>
          </tr>
        ))}</tbody>
      </table>
    </Section>
  );
}
